import { useState } from "react";
import { Check, Lock, Clock, Edit2, Trash2, Trophy, Calendar, Award, Eye, UserRound, X, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarWithYear } from "@/components/ui/calendar-with-year";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import TagBadge from "@/components/TagBadge";
import AchievementImage from "@/components/AchievementImage";
import RarityBadge from "@/components/RarityBadge";
import { FactsPanel, FactIconButton } from "@/components/AchievementFacts";
import { AchievementNotesButton, AchievementNotesPanel } from "./AchievementNotes";
import { format } from "date-fns";
import { isValidUnlockDate, formatDateInput, formatTimeInput, getMinValidDate } from "@/utils/dateUtils";

// Trophy types with their colors
const TROPHY_TYPES = [
  { value: "bronze", label: "Bronze", score: 15, color: "#CD7F32" },
  { value: "silver", label: "Silver", score: 30, color: "#C0C0C0" },
  { value: "gold", label: "Gold", score: 90, color: "#FFD700" },
  { value: "platinum", label: "Platinum", score: 300, color: "#E5E4E2" },
];

// Helper function to convert 24-hour time to 12-hour AM/PM format
const formatTime12Hour = (time24) => {
  if (!time24) return "";
  const [hours, minutes] = time24.split(":").map(Number);
  if (isNaN(hours) || isNaN(minutes)) return time24;
  const period = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
};

// Split a stored `unlockedAt` (an ISO-ish string like "2026-02-13T14:30" or
// "2026-02-13T14:30:00.000Z") into a date and a 24-hour HH:MM string. Either
// piece can come back as null if the stored value is missing or invalid.
function splitUnlockedAt(iso) {
  if (!iso) return { date: null, time: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    // Fallback for values that arrive as bare "YYYY-MM-DDTHH:MM" without any
    // timezone information — parse manually so we don't get NaN on Safari.
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);
    if (!m) return { date: null, time: "" };
    const [, y, mo, day, hh, mm] = m;
    return {
      date: new Date(Number(y), Number(mo) - 1, Number(day)),
      time: hh && mm ? `${hh}:${mm}` : "",
    };
  }
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const midnight = d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0;
  return {
    date: new Date(d.getFullYear(), d.getMonth(), d.getDate()),
    time: midnight ? "" : `${hh}:${mm}`,
  };
}

// Combine a local Date + optional "HH:MM" into an ISO-ish string suitable for
// the backend. Format matches what the datetime-local input previously
// produced ("YYYY-MM-DDTHH:MM"), so no backend change is required.
function joinUnlockedAt(dateObj, hhmm) {
  if (!dateObj) return null;
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  const d = String(dateObj.getDate()).padStart(2, "0");
  const t = /^\d{1,2}:\d{2}$/.test(hhmm || "")
    ? hhmm.split(":").map((p) => p.padStart(2, "0")).join(":")
    : "00:00";
  return `${y}-${m}-${d}T${t}`;
}

// Inline editor for a single per-character unlock timestamp. Deliberately
// mirrors the two-field UX (MM/DD/YYYY text with calendar picker + HH:MM
// 24-hour text with 12-hour preview) used by the achievement unlock dialog
// so users get the exact same input behavior everywhere in the app — no
// browser-native datetime-local weirdness.
// The `onRemove` callback deletes this character's unlock entry entirely
// (surfaced as a labelled destructive button inside the popover so users
// don't have to hunt for the tiny "X" on the row).
function CharacterUnlockTimestampEditor({ value, onChange, onRemove, testIdBase, verified = false }) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const initialSplit = splitUnlockedAt(value);
  const [dateText, setDateText] = useState(initialSplit.date ? format(initialSplit.date, "MM/dd/yyyy") : "");
  const [selectedDate, setSelectedDate] = useState(initialSplit.date);
  const [timeText, setTimeText] = useState(initialSplit.time);
  const [error, setError] = useState("");
  // idle | saving | saved | error — drives the explicit save confirmation UI.
  const [saveState, setSaveState] = useState("idle");
  // Two-step confirm before deleting this character's unlock.
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const syncFromValue = () => {
    const fresh = splitUnlockedAt(value);
    setSelectedDate(fresh.date);
    setDateText(fresh.date ? format(fresh.date, "MM/dd/yyyy") : "");
    setTimeText(fresh.time);
    setError("");
    setSaveState("idle");
  };

  // Opening resets to the latest stored value; closing WITHOUT saving discards
  // local edits (only "Done" persists), so re-sync to the stored value.
  const handleOpenChange = (next) => {
    setPopoverOpen(next);
    if (next) syncFromValue();
    else if (saveState !== "saved") syncFromValue();
  };

  // NOTE: editing only updates LOCAL state now — nothing is sent to the server
  // until the user clicks "Done" (see handleDone). This makes saving explicit
  // and confirmable instead of firing on every keystroke.
  const handleDateTextChange = (raw) => {
    const formatted = formatDateInput(raw);
    setDateText(formatted);
    setSaveState("idle");
    if (formatted.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [mm, dd, yy] = formatted.split("/");
      const parsed = new Date(`${yy}-${mm}-${dd}T12:00:00`);
      if (!Number.isNaN(parsed.getTime())) {
        setSelectedDate(parsed);
        setError("");
        return;
      }
      setError("Invalid date");
    } else if (formatted === "") {
      setSelectedDate(null);
      setError("");
    }
  };

  const handleTimeChange = (raw) => {
    const formatted = formatTimeInput(raw);
    setTimeText(formatted);
    setSaveState("idle");
  };

  const handleCalendarSelect = (d) => {
    setSaveState("idle");
    if (!d) {
      setSelectedDate(null);
      setDateText("");
      return;
    }
    setSelectedDate(d);
    setDateText(format(d, "MM/dd/yyyy"));
    setError("");
  };

  const timeValid = !timeText || /^\d{1,2}:\d{2}$/.test(timeText);

  // Persist on "Done": save, WAIT for the server to confirm, then close. On
  // failure keep the popover open with an error so nothing is silently lost.
  const handleDone = async () => {
    if (selectedDate && !timeValid) {
      setError("Enter time as HH:MM (24-hour)");
      return;
    }
    const iso = selectedDate ? joinUnlockedAt(selectedDate, timeText) : null;
    setSaveState("saving");
    setError("");
    try {
      const ok = await onChange(iso);
      if (ok === false) {
        setSaveState("error");
        setError("Save failed — please try again");
        return;
      }
      setSaveState("saved");
      setTimeout(() => {
        setSaveState("idle");
        setPopoverOpen(false);
      }, 750);
    } catch (e) {
      setSaveState("error");
      setError("Save failed — please try again");
    }
  };

  const doRemove = async () => {
    setConfirmingRemove(false);
    setSaveState("saving");
    try {
      await onRemove?.();
      setSaveState("idle");
      setPopoverOpen(false);
    } catch (e) {
      setSaveState("error");
      setError("Remove failed — please try again");
    }
  };

  const summary = selectedDate
    ? `${format(selectedDate, "MMM d, yyyy")}${timeText ? " · " + formatTime12Hour(timeText) : ""}`
    : "Unlocked (no date)";

  return (
    <Popover open={popoverOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`mt-0.5 inline-flex items-center gap-1 text-[10px] rounded px-1.5 py-0.5 border ${
            selectedDate
              ? "bg-pink-500/10 border-pink-400/30 text-pink-200"
              : "bg-emerald-500/10 border-emerald-400/30 text-emerald-200 hover:bg-emerald-500/20"
          }`}
          data-testid={`${testIdBase}-trigger`}
          title={selectedDate ? "Edit unlock date & time" : "Unlocked — click to add a date & time"}
        >
          {selectedDate ? (
            <Clock className="w-2.5 h-2.5" />
          ) : (
            <Check className="w-2.5 h-2.5" />
          )}
          <span>{summary}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-3 bg-[#0A0A0A] border-white/10 text-white space-y-3"
        data-testid={`${testIdBase}-popover`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-1.5">
          <Label className="text-xs">Unlock Date</Label>
          <div className="flex gap-1.5">
            <Input
              type="text"
              value={dateText}
              onChange={(e) => handleDateTextChange(e.target.value)}
              className="bg-black/50 border-white/10 flex-1 h-8 text-xs"
              placeholder="MM/DD/YYYY"
              maxLength={10}
              data-testid={`${testIdBase}-date-input`}
            />
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="icon" className="border-white/10 bg-black/50 h-8 w-8 shrink-0">
                  <Calendar className="w-3.5 h-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#0A0A0A] border-white/10">
                <CalendarWithYear
                  selected={selectedDate || undefined}
                  onSelect={handleCalendarSelect}
                  fromYear={getMinValidDate().getFullYear()}
                  toYear={new Date().getFullYear()}
                />
              </PopoverContent>
            </Popover>
          </div>
          {error && <p className="text-[10px] text-red-400">{error}</p>}
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Unlock Time (optional)</Label>
          <Input
            type="text"
            value={timeText}
            onChange={(e) => handleTimeChange(e.target.value)}
            className="bg-black/50 border-white/10 h-8 text-xs"
            placeholder="HH:MM (e.g., 14:30)"
            maxLength={5}
            data-testid={`${testIdBase}-time-input`}
          />
          {timeText && (
            <p className={`text-[10px] ${timeValid ? "text-green-400" : "text-amber-400"}`}>
              {timeValid
                ? `Displays as ${formatTime12Hour(timeText)}`
                : "Enter time as HH:MM (24-hour)"}
            </p>
          )}
        </div>

        {confirmingRemove ? (
          <div className="pt-2 border-t border-red-400/20 space-y-2" data-testid={`${testIdBase}-remove-confirm`}>
            <p className="text-[11px] text-red-200 leading-snug">
              {verified
                ? "Achievement has been confirmed as unlocked. Are you sure you wish to remove this character's unlock?"
                : "Remove this character's unlock? This clears their date for this achievement."}
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className="text-[11px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-white/70 hover:bg-white/10"
                onClick={() => setConfirmingRemove(false)}
                data-testid={`${testIdBase}-remove-cancel`}
              >
                Cancel
              </button>
              <button
                type="button"
                className="text-[11px] px-2.5 py-0.5 rounded bg-red-500 hover:bg-red-600 text-white"
                onClick={doRemove}
                data-testid={`${testIdBase}-remove-confirm-yes`}
              >
                Yes, remove
              </button>
            </div>
          </div>
        ) : (
        <div className="flex items-center justify-between pt-2 border-t border-white/5">
          <button
            type="button"
            className="text-[11px] px-2 py-0.5 rounded bg-red-500/15 border border-red-400/30 text-red-200 hover:bg-red-500/30 disabled:opacity-50"
            onClick={() => setConfirmingRemove(true)}
            disabled={saveState === "saving"}
            data-testid={`${testIdBase}-remove`}
            title="Delete this character's unlock entirely"
          >
            Remove Unlock
          </button>
          <div className="flex items-center gap-2">
            {saveState === "saved" ? (
              <span className="text-[11px] text-emerald-300 inline-flex items-center gap-1" data-testid={`${testIdBase}-saved`}>
                <Check className="w-3 h-3" /> Saved
              </span>
            ) : saveState === "error" ? (
              <span className="text-[11px] text-red-300" data-testid={`${testIdBase}-save-error`}>Save failed</span>
            ) : (
              <button
                type="button"
                className="text-[11px] text-white/50 hover:text-white/80 disabled:opacity-50"
                onClick={() => { setSelectedDate(null); setDateText(""); setTimeText(""); setError(""); setSaveState("idle"); }}
                disabled={saveState === "saving"}
                data-testid={`${testIdBase}-clear`}
                title="Keep the unlock but remove its date and time (click Done to save)"
              >
                Clear date/time
              </button>
            )}
            <button
              type="button"
              className="text-[11px] px-2.5 py-0.5 rounded bg-pink-500/20 border border-pink-400/30 text-pink-100 hover:bg-pink-500/30 disabled:opacity-60 inline-flex items-center gap-1"
              onClick={handleDone}
              disabled={saveState === "saving" || saveState === "saved"}
              data-testid={`${testIdBase}-done`}
            >
              {saveState === "saving" ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" /> Saving…
                </>
              ) : saveState === "saved" ? (
                <>
                  <Check className="w-3 h-3" /> Saved
                </>
              ) : (
                "Done"
              )}
            </button>
          </div>
        </div>
        )}
      </PopoverContent>
    </Popover>
  );
}


// Verification badge for achievement cards
function VerificationBadge({ achievement }) {
  const status = achievement.verificationStatus;
  const isCustom = achievement.isCustom;
  
  if (isCustom || status === "custom") {
    return (
      <span
        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-zinc-600/40 text-[9px] font-bold text-zinc-400 border border-zinc-500/30 shrink-0"
        title="Custom achievement — not counted in official stats"
        data-testid="verification-badge-custom"
      >
        C
      </span>
    );
  }
  
  if (status === "verified") {
    return (
      <Award
        className="w-4 h-4 text-emerald-400 shrink-0"
        title={`Verified via ${achievement.verificationSource || "platform sync"}`}
        data-testid="verification-badge-verified"
      />
    );
  }
  
  if (status === "secondary_verified") {
    return (
      <Award
        className="w-4 h-4 text-amber-400 shrink-0"
        title={`Secondary verification via ${achievement.verificationSource || "alternative source"}`}
        data-testid="verification-badge-secondary"
      />
    );
  }
  
  if (status === "unverified" && achievement.isUnlocked) {
    return (
      <Award
        className="w-4 h-4 text-zinc-500 shrink-0"
        title="Unverified — manually marked as unlocked, not counted in official stats"
        data-testid="verification-badge-unverified"
      />
    );
  }
  
  return null;
}

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const MILESTONE_COLORS = {
  achievement: "#7C3AED",
  score: "#F59E0B",
};
const MILESTONE_PLATFORM_COLORS = {
  xbox: "#107C10",
  playstation: "#2E6DB4",
  steam: "#66C0F4",
};

function MilestoneBadge({ milestones }) {
  if (!milestones || milestones.length === 0) return null;
  // Pick primary color: first milestone's type/platform determines it
  const primary = milestones[0];
  const color = primary.platform
    ? (MILESTONE_PLATFORM_COLORS[primary.platform] || MILESTONE_COLORS[primary.type] || "#7C3AED")
    : (MILESTONE_COLORS[primary.type] || "#7C3AED");

  return (
    <TooltipProvider>
      <Tooltip delayDuration={150}>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center justify-center w-4 h-4 rounded-full shrink-0 cursor-help text-[7px] font-bold"
            style={{ backgroundColor: `${color}30`, color: color, border: `1.5px solid ${color}60` }}
            data-testid="milestone-badge"
          >
            M
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs bg-[#1a1a1a] border-white/10 p-2">
          <p className="text-[10px] text-white/50 mb-1 font-medium">Milestones</p>
          <div className="space-y-0.5">
            {milestones.map((m, i) => {
              const c = m.platform
                ? (MILESTONE_PLATFORM_COLORS[m.platform] || MILESTONE_COLORS[m.type] || "#7C3AED")
                : (MILESTONE_COLORS[m.type] || "#7C3AED");
              return (
                <p key={i} className="text-xs font-medium" style={{ color: c }}>{m.label}</p>
              );
            })}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function AchievementCard({
  achievement,
  game,
  tags,
  scoreAbbr,
  onToggleUnlock,
  onEditTimestamp,
  onEdit,
  onDelete,
  // Facts props
  facts = [],
  factCount = 0,
  showFacts = true,
  onFactsClick,
  hasMasterGame = false, // Whether the game is linked to master database
  // DLC tag props - for showing DLC source when in organizer view
  showDlcTag = false,
  dlcName = null,
  noteCount = 0,
  onPlannerChange,
  // Milestone props
  milestones = null,
  userPrefs = {},
  // Per-character unlocks — passed in from parent to keep this card
  // presentation-focused. `characterNotes` is the pre-filtered list of
  // character-typed notes on the parent game; `onUpdatePerCharacter`
  // persists the new unlocks list via a PATCH.
  characterNotes = [],
  onUpdatePerCharacter,
  onSetCharacterUnlock,
  onRemoveCharacterUnlock,
}) {
  const [factsOpen, setFactsOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  // Per-card "reveal secret" state — lets the user peek at a single hidden
  // achievement without changing their global hide-secret preferences.
  // Resets on page navigation since it's local component state.
  const [revealed, setRevealed] = useState(false);
  const [revealDialogOpen, setRevealDialogOpen] = useState(false);
  // Confirmation before re-locking (removing) an unlock — stronger wording
  // when the achievement is verified/confirmed, but removal is still allowed.
  const [confirmLockOpen, setConfirmLockOpen] = useState(false);
  const isVerifiedAch =
    achievement.verificationStatus === "verified" || achievement.verificationStatus === "secondary_verified";
  const requestToggleUnlock = () => {
    if (achievement.isUnlocked) {
      setConfirmLockOpen(true); // removing an unlock → confirm first
    } else {
      onToggleUnlock(achievement); // unlocking → opens the timestamp dialog
    }
  };

  // Secret-achievement hiding
  // ─────────────────────────────────────────────────────────────────
  // When the achievement is marked secret AND still locked AND the user
  // hasn't turned secret-hiding off in Settings, suppress the parts they
  // chose to hide (image / name+description). Unlocking flips this off
  // since the user has presumably seen the reveal on their platform.
  // The per-card `revealed` toggle bypasses hiding for this one card only.
  const isLockedSecret =
    !!achievement.isSecret &&
    !achievement.isUnlocked &&
    userPrefs.hideSecretAchievements !== false;
  const effectivelyHidden = isLockedSecret && !revealed;
  const hideImage = effectivelyHidden && userPrefs.hideSecretImage !== false;
  // We treat the description as visually part of the "name" privacy unit:
  // showing the description while hiding the name would spoil the reveal.
  const hideName = effectivelyHidden && userPrefs.hideSecretName !== false;
  const displayName = hideName ? "Secret Achievement" : achievement.name;
  const displayDescription = hideName ? null : achievement.description;
  
  const handleFactsClick = () => {
    if (!hasMasterGame) {
      // Game not linked to master database - can't use facts yet
      return;
    }
    if (factCount > 0) {
      setFactsOpen(!factsOpen);
    } else if (onFactsClick) {
      onFactsClick(achievement);
    }
  };
  
  const hasMilestones = milestones && milestones.length > 0;
  const milestoneColor = hasMilestones
    ? (milestones[0].platform
        ? (MILESTONE_PLATFORM_COLORS[milestones[0].platform] || "#7C3AED")
        : (MILESTONE_COLORS[milestones[0].type] || "#7C3AED"))
    : null;
  
  return (
    <div
      key={achievement.id}
      data-testid={`achievement-${achievement.id}`}
      className={`relative p-3 sm:p-4 rounded-lg transition-all ${
        achievement.isUnlocked
          ? "bg-green-500/15 border border-green-500/30"
          : "bg-[#121212] border border-white/5"
      }`}
      style={hasMilestones ? { boxShadow: `inset 0 0 0 1.5px ${milestoneColor}50, 0 0 8px ${milestoneColor}15` } : undefined}
    >
      {/* Main content row */}
      <div className="flex items-start gap-3 sm:gap-4">
        {/* Achievement Image — replaced with a lock icon when this is a
            locked secret achievement and the user has chosen to hide the
            image. */}
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded bg-black/30 overflow-hidden shrink-0">
          {hideImage ? (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-500/10 to-amber-900/20 border border-amber-500/20">
              <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400/80" />
            </div>
          ) : achievement.imageUrl ? (
            <AchievementImage
              src={achievement.imageUrl}
              additionalImages={achievement.additionalImages}
              alt={displayName}
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-white/20" />
            </div>
          )}
        </div>

        {/* Achievement Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4
              className={`font-medium text-sm sm:text-base sm:truncate sm:max-w-none ${
                hideName ? "text-amber-300/80 italic" : "text-white"
              }`}
            >
              {displayName}
            </h4>
            {/* Secret badge — clearly tells the user why the name is hidden */}
            {isLockedSecret && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-400/30 font-semibold uppercase tracking-wide flex-shrink-0"
                title={revealed
                  ? "Revealed — this achievement is marked Secret."
                  : "This achievement is marked Secret. Unlock it to reveal."}
                data-testid={`secret-badge-${achievement.id}`}
              >
                Secret
              </span>
            )}
            {/* Reveal button — only shown while the secret is still hidden */}
            {isLockedSecret && !revealed && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setRevealDialogOpen(true);
                }}
                className="inline-flex items-center justify-center h-5 px-1.5 rounded text-[10px] gap-1 bg-amber-500/10 hover:bg-amber-500/25 text-amber-300 border border-amber-400/30 transition-colors flex-shrink-0"
                title="Reveal this secret achievement"
                data-testid={`reveal-secret-btn-${achievement.id}`}
              >
                <Eye className="w-3 h-3" />
                <span className="font-semibold uppercase tracking-wide">Reveal</span>
              </button>
            )}
            {/* Verification badge */}
            {achievement.isUnlocked && <VerificationBadge achievement={achievement} />}
            {/* Milestone badge */}
            {hasMilestones && <MilestoneBadge milestones={milestones} />}
            {/* Rarity badge */}
            {achievement.rarity != null && (
              <RarityBadge rarity={achievement.rarity} size="xs" />
            )}
            {/* Trophy indicator for PlayStation */}
            {game?.platform === "playstation" && achievement.trophyType && (
              <span
                className="w-3 h-3 sm:w-4 sm:h-4 rounded-full"
                style={{ backgroundColor: TROPHY_TYPES.find(t => t.value === achievement.trophyType)?.color }}
                title={achievement.trophyType}
              />
            )}
            {/* DLC tag - shown when achievement is from DLC but displayed in organizer */}
            {showDlcTag && dlcName && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30 whitespace-nowrap">
                {dlcName}
              </span>
            )}
          </div>
          {displayDescription && (
            <p className="text-white/50 text-xs sm:text-sm break-words">{displayDescription}</p>
          )}
          <div className="flex items-center gap-2 sm:gap-3 mt-1 text-xs text-white/40 flex-wrap">
            <span className="text-yellow-400">{achievement.score} {scoreAbbr}</span>
            {achievement.isChallenge && (
              <span
                title="Legacy Xbox Challenge (discontinued — worth 0 Gamerscore)"
                data-testid="achievement-challenge-badge"
                className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30"
              >
                Challenge
              </span>
            )}
            {achievement.isUnlocked && achievement.earnedAt && isValidUnlockDate(achievement.earnedAt, game?.releaseDate) && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span className="hidden sm:inline">
                  {achievement.earnedTime 
                    ? format(new Date(achievement.earnedAt), "MMM d, yyyy") + " at " + formatTime12Hour(achievement.earnedTime)
                    : (() => {
                        const earnedDate = new Date(achievement.earnedAt);
                        const hasTime = earnedDate.getHours() !== 0 || earnedDate.getMinutes() !== 0;
                        return hasTime 
                          ? format(earnedDate, "MMM d, yyyy") + " at " + format(earnedDate, "h:mm a")
                          : format(earnedDate, "MMM d, yyyy");
                      })()
                  }
                </span>
                <span className="sm:hidden">
                  {achievement.earnedTime 
                    ? format(new Date(achievement.earnedAt), "MMM d") + " " + formatTime12Hour(achievement.earnedTime)
                    : (() => {
                        const earnedDate = new Date(achievement.earnedAt);
                        const hasTime = earnedDate.getHours() !== 0 || earnedDate.getMinutes() !== 0;
                        return hasTime 
                          ? format(earnedDate, "MMM d") + " " + format(earnedDate, "h:mm a")
                          : format(earnedDate, "MMM d");
                      })()
                  }
                </span>
                {achievement.unlockedOffline && (
                  <span className="text-amber-400 ml-1">(Offline)</span>
                )}
              </span>
            )}
            {achievement.isUnlocked && (!achievement.earnedAt || !isValidUnlockDate(achievement.earnedAt, game?.releaseDate)) && (
              <span className="flex items-center gap-1 text-white/30 italic">
                <Calendar className="w-3 h-3" />
                Date unknown
              </span>
            )}
          </div>
          {/* Achievement Tags */}
          {achievement.tags?.length > 0 && (
            <div className="flex items-center gap-1 mt-1 flex-wrap">
              {achievement.tags
                .map((tagId) => tags.find((t) => t.id === tagId))
                .filter((tag) => tag && tag.name?.toLowerCase() !== "incomplete sync")
                .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
                .map((tag) => tag ? <TagBadge key={tag.id} tag={tag} size="sm" /> : null)
              }
            </div>
          )}
        </div>
      </div>

      {/* Actions Row - Always visible, on separate row on mobile */}
      <div className="flex items-center justify-end gap-2 mt-2 pt-2 border-t border-white/5 sm:hidden flex-wrap relative z-10">
        {/* Facts Button */}
        {showFacts && (
          <FactIconButton
            factCount={factCount}
            onClick={handleFactsClick}
            size="sm"
            disabled={!hasMasterGame}
            title={!hasMasterGame ? "Link this game to Master DB to use facts" : undefined}
          />
        )}

        {/* Notes Button */}
        <AchievementNotesButton
          count={noteCount}
          onClick={() => setNotesOpen(!notesOpen)}
          size="sm"
        />
        
        {/* Toggle Unlock */}
        {(() => {
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={requestToggleUnlock}
              className={`h-10 w-10 p-0 shrink-0 ${achievement.isUnlocked ? "text-green-400" : "text-white/40"}`}
              data-testid={`toggle-unlock-mobile-${achievement.id}`}
              title={achievement.isUnlocked ? "Remove unlock (lock this achievement)" : "Mark as unlocked"}
            >
              {achievement.isUnlocked ? <Check className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
            </Button>
          );
        })()}

        {/* Edit Timestamp */}
        {achievement.isUnlocked && (() => {
          const isVerified = achievement.verificationStatus === "verified" || achievement.verificationStatus === "secondary_verified";
          const canEditTimestamp = !isVerified || achievement.unlockedOffline || !achievement.earnedAt || !achievement.isPerCharacter;
          return canEditTimestamp ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEditTimestamp(achievement)}
              className="text-white/40 hover:text-white h-10 w-10 p-0 shrink-0"
              data-testid={`edit-timestamp-mobile-${achievement.id}`}
            >
              <Clock className="w-5 h-5" />
            </Button>
          ) : null;
        })()}

        {/* Edit */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(achievement)}
          className="text-white/40 hover:text-white h-10 w-10 p-0 shrink-0"
          data-testid={`edit-achievement-mobile-${achievement.id}`}
        >
          <Edit2 className="w-5 h-5" />
        </Button>

        {/* Delete */}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="sm" className="text-red-400/60 hover:text-red-400 h-10 w-10 p-0 shrink-0">
              <Trash2 className="w-5 h-5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-[#0A0A0A] border-white/10">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete Achievement?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete &quot;{achievement.name}&quot;. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-transparent border-white/10">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onDelete(achievement.id)}
                className="bg-red-500 hover:bg-red-600"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Desktop Actions - hidden on mobile */}
      <div className="hidden sm:flex items-center gap-1 absolute top-3 right-3">
        {/* Facts Button */}
        {showFacts && (
          <FactIconButton
            factCount={factCount}
            onClick={handleFactsClick}
            size="default"
            disabled={!hasMasterGame}
            title={!hasMasterGame ? "Link this game to Master DB to use facts" : undefined}
          />
        )}

        {/* Notes Button */}
        <AchievementNotesButton
          count={noteCount}
          onClick={() => setNotesOpen(!notesOpen)}
        />
        
        {/* Toggle Unlock */}
        {(() => {
          return (
            <Button
              variant="ghost"
              size="icon"
              onClick={requestToggleUnlock}
              className={`h-8 w-8 ${achievement.isUnlocked ? "text-green-400" : "text-white/40"}`}
              data-testid={`toggle-unlock-desktop-${achievement.id}`}
              title={achievement.isUnlocked ? "Remove unlock (lock this achievement)" : "Mark as unlocked"}
            >
              {achievement.isUnlocked ? <Check className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
            </Button>
          );
        })()}

        {/* Edit Timestamp */}
        {achievement.isUnlocked && (() => {
          const isVerified = achievement.verificationStatus === "verified" || achievement.verificationStatus === "secondary_verified";
          const canEditTimestamp = !isVerified || achievement.unlockedOffline || !achievement.earnedAt || !achievement.isPerCharacter;
          return canEditTimestamp ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEditTimestamp(achievement)}
              className="text-white/40 hover:text-white h-8 w-8"
              data-testid="edit-timestamp-btn-desktop"
            >
              <Clock className="w-3 h-3" />
            </Button>
          ) : null;
        })()}

        {/* Edit */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onEdit(achievement)}
          className="text-white/40 hover:text-white h-8 w-8"
          data-testid={`edit-achievement-desktop-${achievement.id}`}
        >
          <Edit2 className="w-3 h-3" />
        </Button>

        {/* Delete */}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="icon" className="text-red-400/60 hover:text-red-400 h-8 w-8">
              <Trash2 className="w-3 h-3" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-[#0A0A0A] border-white/10">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete Achievement?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete &quot;{achievement.name}&quot;. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-transparent border-white/10">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onDelete(achievement.id)}
                className="bg-red-500 hover:bg-red-600"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      
      {/* Facts Panel */}
      {showFacts && hasMasterGame && (
        <FactsPanel
          facts={facts}
          isOpen={factsOpen}
          onToggle={setFactsOpen}
          onSubmitClick={() => onFactsClick && onFactsClick(achievement)}
          achievementName={achievement.name}
        />
      )}

      {/* Achievement Notes Panel */}
      <AchievementNotesPanel
        achievementId={achievement.id}
        gameId={game?.id}
        isOpen={notesOpen}
        onToggle={setNotesOpen}
        onPlannerChange={onPlannerChange}
      />

      {/* Per-Character Unlocks — surfaced when the master achievement is
          flagged `isPerCharacter`. Users pick from character notes on this
          game and can optionally record when each character earned it.
          Score/stats still count only the primary `isUnlocked` once. */}
      {achievement.isPerCharacter && (
        <PerCharacterSection
          achievement={achievement}
          gameId={game?.id}
          characterNotes={characterNotes}
          onUpdate={onUpdatePerCharacter}
          onSetUnlock={onSetCharacterUnlock}
          onRemoveUnlock={onRemoveCharacterUnlock}
        />
      )}

      {/* Reveal Secret Confirmation */}
      <AlertDialog open={revealDialogOpen} onOpenChange={setRevealDialogOpen}>
        <AlertDialogContent className="bg-[#0A0A0A] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Eye className="w-4 h-4 text-amber-400" />
              Reveal this secret achievement?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This achievement is marked Secret. Revealing it will show its
              name, description, and image on this card. This only affects this
              card during your current session — your Settings won&apos;t change.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-transparent border-white/10"
              data-testid={`reveal-secret-cancel-${achievement.id}`}
            >
              No, keep it hidden
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => setRevealed(true)}
              className="bg-amber-500 hover:bg-amber-600 text-black"
              data-testid={`reveal-secret-confirm-${achievement.id}`}
            >
              Yes, show me
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm removing (re-locking) an unlock — stronger wording when verified */}
      <AlertDialog open={confirmLockOpen} onOpenChange={setConfirmLockOpen}>
        <AlertDialogContent className="bg-[#0A0A0A] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-400" />
              Remove this unlock?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isVerifiedAch
                ? "Achievement has been confirmed as unlocked. Are you sure you wish to remove it? This will lock the achievement and clear its verified status."
                : "This will lock the achievement and clear its unlock date. You can mark it unlocked again later."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-transparent border-white/10"
              data-testid={`confirm-lock-cancel-${achievement.id}`}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => onToggleUnlock(achievement, isVerifiedAch)}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid={`confirm-lock-remove-${achievement.id}`}
            >
              Yes, remove unlock
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Export trophy types and milestone components for reuse
function PerCharacterSection({ achievement, gameId, characterNotes, onUpdate, onSetUnlock, onRemoveUnlock }) {
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  // noteId pending confirmation for the quick "X" remove button.
  const [pendingRemove, setPendingRemove] = useState(null);
  const achVerified =
    achievement.verificationStatus === "verified" || achievement.verificationStatus === "secondary_verified";
  // Multi-select buffer for the "Add character unlock" dropdown. Users can
  // tick as many characters as they want before committing so a five-hero
  // run stamps in one save instead of five.
  const [pickedIds, setPickedIds] = useState(() => new Set());
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 5;
  const unlocks = [...(achievement.perCharacterUnlocks || [])].sort((a, b) => {
    // Characters with NO timestamp come first (easy to find/fill), then the
    // rest ordered by earliest unlock date.
    const ta = a?.unlockedAt ? new Date(a.unlockedAt).getTime() : NaN;
    const tb = b?.unlockedAt ? new Date(b.unlockedAt).getTime() : NaN;
    const aNo = Number.isNaN(ta);
    const bNo = Number.isNaN(tb);
    if (aNo !== bNo) return aNo ? -1 : 1; // no-timestamp first
    if (aNo && bNo) return 0; // both undated — keep order
    return ta - tb; // earliest first
  });
  const notesById = Object.fromEntries((characterNotes || []).map((n) => [n.id, n]));
  const unlockedIds = new Set(unlocks.map((u) => u.characterNoteId));
  const availableToAdd = (characterNotes || []).filter((n) => !unlockedIds.has(n.id));

  const togglePick = (noteId) => {
    setPickedIds((prev) => {
      const next = new Set(prev);
      if (next.has(noteId)) next.delete(noteId);
      else next.add(noteId);
      return next;
    });
  };

  const toggleAllPicks = () => {
    setPickedIds((prev) => {
      if (prev.size === availableToAdd.length) return new Set();
      return new Set(availableToAdd.map((n) => n.id));
    });
  };

  const commitPicks = () => {
    if (pickedIds.size === 0) return;
    // Add each picked character as its own atomic upsert (unlocked date empty).
    Array.from(pickedIds).forEach((noteId) => {
      if (onSetUnlock) onSetUnlock(achievement.id, noteId, null, gameId);
      else if (onUpdate) onUpdate(achievement.id, [...unlocks, { characterNoteId: noteId, gameId, unlockedAt: null }]);
    });
    setPickedIds(new Set());
    setAddOpen(false);
    setOpen(true);
  };

  // Close the picker without saving.
  const cancelPicks = () => {
    setPickedIds(new Set());
    setAddOpen(false);
  };

  const removeUnlock = (noteId) => {
    // Atomic single-entry delete so it can't clobber other pending edits.
    if (onRemoveUnlock) return onRemoveUnlock(achievement.id, noteId);
    if (onUpdate) return onUpdate(achievement.id, unlocks.filter((u) => u.characterNoteId !== noteId));
  };
  const setUnlockedAt = (noteId, isoOrNull) => {
    // Atomic single-entry upsert so rapid edits to different characters never
    // overwrite one another (fixes the whole-array lost-update race).
    if (onSetUnlock) return onSetUnlock(achievement.id, noteId, isoOrNull, gameId);
    if (onUpdate) return onUpdate(achievement.id, unlocks.map((u) => (u.characterNoteId === noteId ? { ...u, unlockedAt: isoOrNull } : u)));
  };

  return (
    <div className="mt-1 border-t border-white/5" data-testid={`per-character-section-${achievement.id}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-2 py-1.5 text-xs hover:bg-white/5 transition-colors"
        data-testid={`per-character-toggle-${achievement.id}`}
      >
        <span className="flex items-center gap-1.5">
          <UserRound className="w-3 h-3" style={{ color: "#e11d74" }} />
          <span className="text-white/70">Per Character</span>
          {unlocks.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-pink-500/15 text-pink-300 border border-pink-400/20">
              {unlocks.length}
            </span>
          )}
        </span>
        <span className="text-white/30 text-[10px]">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="px-2 pb-2 space-y-1.5">
          {unlocks.length === 0 && (
            <p className="text-[11px] text-white/30 italic">No characters have earned this yet.</p>
          )}
          {(() => {
            const pageCount = Math.max(1, Math.ceil(unlocks.length / PAGE_SIZE));
            const safePage = Math.min(page, pageCount - 1);
            const paginate = unlocks.length > PAGE_SIZE;
            const rows = paginate ? unlocks.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE) : unlocks;
            return (
              <>
                {rows.map((u) => {
                  const n = notesById[u.characterNoteId];
                  const imgUrl = (n?.images || [])[0];
                  return (
                    <div
                      key={u.characterNoteId}
                      className="flex items-center gap-2 px-1.5 py-1 rounded bg-black/40 border border-white/5"
                      data-testid={`per-character-row-${u.characterNoteId}`}
                    >
                      <div className="w-7 h-7 rounded overflow-hidden bg-black/60 border border-white/10 shrink-0 flex items-center justify-center">
                        {imgUrl ? (
                          <img src={imgUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <UserRound className="w-3 h-3 text-white/30" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0 text-xs">
                        <div className="text-white/85 truncate">{n?.title || "Unknown character"}</div>
                        <CharacterUnlockTimestampEditor
                          value={u.unlockedAt}
                          onChange={(iso) => setUnlockedAt(u.characterNoteId, iso)}
                          onRemove={() => removeUnlock(u.characterNoteId)}
                          testIdBase={`per-character-date-${u.characterNoteId}`}
                          verified={achVerified}
                        />
                      </div>
                      <button
                        onClick={() => setPendingRemove(u.characterNoteId)}
                        className="text-white/30 hover:text-red-400 shrink-0"
                        title="Remove"
                        data-testid={`per-character-remove-${u.characterNoteId}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
                {paginate && (
                  <div className="flex items-center justify-between pt-1" data-testid={`per-character-pagination-${achievement.id}`}>
                    <button
                      type="button"
                      onClick={() => setPage(Math.max(0, safePage - 1))}
                      disabled={safePage === 0}
                      className="text-[11px] px-2 py-0.5 rounded border border-white/10 text-white/60 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`per-character-prev-${achievement.id}`}
                    >
                      Prev
                    </button>
                    <span className="text-[10px] text-white/40">
                      {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, unlocks.length)} of {unlocks.length}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))}
                      disabled={safePage >= pageCount - 1}
                      className="text-[11px] px-2 py-0.5 rounded border border-white/10 text-white/60 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`per-character-next-${achievement.id}`}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            );
          })()}
          {availableToAdd.length > 0 && (
            <Popover open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) setPickedIds(new Set()); }}>
              <PopoverTrigger asChild>
                <button
                  className="text-[11px] px-2 py-1 rounded border border-pink-400/30 bg-pink-500/10 text-pink-300 hover:bg-pink-500/20 transition-colors flex items-center gap-1"
                  data-testid={`per-character-add-${achievement.id}`}
                >
                  <Plus className="w-3 h-3" /> Add character unlock
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="top"
                align="start"
                sideOffset={4}
                collisionPadding={12}
                className="p-0 bg-[#0A0A0A] border-white/10 w-[260px] max-w-none shadow-xl rounded"
                data-testid={`per-character-picker-${achievement.id}`}
              >
                <div className="flex items-center justify-between px-2 py-1.5 border-b border-white/5">
                  <button
                    type="button"
                    onClick={toggleAllPicks}
                    className="text-[10px] text-pink-300 hover:text-pink-200"
                    data-testid={`per-character-pick-all-${achievement.id}`}
                  >
                    {pickedIds.size === availableToAdd.length ? "Clear all" : "Select all"}
                  </button>
                  <span className="text-[10px] text-white/40">
                    {pickedIds.size} / {availableToAdd.length} selected
                  </span>
                </div>
                <div className="py-1 max-h-52 overflow-y-auto">
                  {availableToAdd.map((n) => {
                    const checked = pickedIds.has(n.id);
                    const imgUrl = (n?.images || [])[0];
                    return (
                      <button
                        key={n.id}
                        onClick={() => togglePick(n.id)}
                        className={`w-full text-left px-2 py-1 text-xs flex items-center gap-2 ${
                          checked ? "bg-pink-500/15 text-pink-100" : "hover:bg-white/5 text-white/80"
                        }`}
                        data-testid={`per-character-pick-${n.id}`}
                      >
                        <span
                          aria-hidden="true"
                          className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm border shrink-0"
                          style={{
                            borderColor: checked ? "#e11d74" : "rgba(255,255,255,0.35)",
                            backgroundColor: checked ? "#e11d74" : "transparent",
                          }}
                        >
                          {checked && (
                            <svg viewBox="0 0 12 12" className="w-3 h-3" fill="none" stroke="#0A0A0A" strokeWidth="2.5">
                              <path d="M2 6 L5 9 L10 3" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </span>
                        <span className="w-4 h-4 rounded overflow-hidden bg-black/60 border border-white/10 shrink-0 flex items-center justify-center">
                          {imgUrl ? (
                            <img src={imgUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <UserRound className="w-2.5 h-2.5 text-white/40" />
                          )}
                        </span>
                        <span className="truncate flex-1">{n.title || "Unnamed"}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between gap-2 px-2 py-1.5 border-t border-white/5">
                  <button
                    type="button"
                    onClick={cancelPicks}
                    className="text-[10px] text-white/50 hover:text-white/80"
                    data-testid={`per-character-cancel-${achievement.id}`}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={commitPicks}
                    disabled={pickedIds.size === 0}
                    className="text-[10px] px-2 py-0.5 rounded bg-pink-500/20 border border-pink-400/30 text-pink-100 hover:bg-pink-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`per-character-commit-${achievement.id}`}
                  >
                    {pickedIds.size === 0
                      ? "Add"
                      : pickedIds.size === 1
                      ? "Add 1 unlock"
                      : `Add ${pickedIds.size} unlocks`}
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          )}
          {characterNotes.length === 0 && (
            <p className="text-[11px] text-white/30 italic">
              Add a Character note on this game first, then you can log per-character unlocks here.
            </p>
          )}
        </div>
      )}

      {/* Confirm quick-remove (X) of a character's unlock */}
      <AlertDialog open={!!pendingRemove} onOpenChange={(o) => { if (!o) setPendingRemove(null); }}>
        <AlertDialogContent className="bg-[#0A0A0A] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <X className="w-4 h-4 text-red-400" />
              Remove this character&apos;s unlock?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {achVerified
                ? "Achievement has been confirmed as unlocked. Are you sure you wish to remove this character's unlock?"
                : "This removes this character's unlock from the achievement. You can add it back later."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-transparent border-white/10"
              data-testid={`per-character-remove-cancel-${achievement.id}`}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { const id = pendingRemove; setPendingRemove(null); if (id) removeUnlock(id); }}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid={`per-character-remove-confirm-${achievement.id}`}
            >
              Yes, remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
export { TROPHY_TYPES, formatTime12Hour, MilestoneBadge, MILESTONE_COLORS, MILESTONE_PLATFORM_COLORS };
